import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, FieldArray, useFormikContext } from 'formik';
import { isEmpty } from 'lodash-es';

import { ReactComponent as CrossIcon } from '../../../../../../assets/images/cross-secondary.svg';
import CustomTextField from '../../../../../../components/shared/CustomTextField/CustomTextField';
import URLTypeSelect from '../../../../../../components/shared/URLTypeSelect/URLTypeSelect';
import { WEB_LINKS } from '../../../../../../constants/strengthenQuestionsConstants';
import { formatURLType } from '../../../../../../helpers/candidateQuestionHelper';
import { validateWebsiteURL } from '../../../../../../helpers/commonHelper';

import styles from './AdditionalURL.module.scss';

const AdditionalURL = () => {
  const { t } = useTranslation(['referral', 'common']);
  const { values, setValues } = useFormikContext();

  // Transform WEB_LINKS to include translated labels
  const translatedWebLinks = useMemo(() => WEB_LINKS.map((link) => ({ ...link, label: t(link.labelKey) })), [t]);

  const handleTypeChange = useCallback(
    (type, index, arrayHelpers) => {
      const newData = [...values.links_json];
      newData[index] = { ...newData[index], type: type.type };
      arrayHelpers.replace(index, newData[index]);
    },
    [values]
  );

  const handleAddField = useCallback(() => {
    const newData = [...values.links_json];
    let allFieldsFilled = true;

    newData.forEach(({ value }) => {
      if (isEmpty(value)) {
        allFieldsFilled = false;
      }
    });

    if (allFieldsFilled) {
      newData.push({ type: 'pw', value: '' });
      setValues({ ...values, links_json: newData });
    }
  }, [values]);

  const handleDropField = useCallback(
    (index, arrayHelpers) => {
      arrayHelpers.remove(index);
    },
    [values]
  );

  return (
    <div>
      <span className={styles.heading}>{t('strengthen.additionalUrl')}</span>
      <FieldArray
        name="links_json"
        render={(arrayHelpers) => (
          <div>
            {values.links_json.map((value, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`link-${index}`}>
                <Field
                  as={URLTypeSelect}
                  name={`links_json[${index}].type`}
                  onChange={(type) => handleTypeChange(type, index, arrayHelpers)}
                  value={formatURLType(value.type)}
                  options={translatedWebLinks}
                  defaultValue={{ label: t('common:strengthen.webLinks.website') }}
                  isClearable={false}
                  isSearchable={false}
                />
                <div className={styles.inputWrapper}>
                  <div className={styles.inputContainer}>
                    <Field
                      as={CustomTextField}
                      name={`links_json[${index}].value`}
                      value={value.value}
                      type="text"
                      label={t('common:placeholders.typeOrPasteUrl')}
                      error={value.value && !validateWebsiteURL(value.value)}
                    />
                  </div>
                  {index > 0 && !isEmpty(value.value) && (
                    <div className={styles.buttonContainer}>
                      <button
                        className={styles.imgButton}
                        type="button"
                        onClick={() => handleDropField(index, arrayHelpers)}
                      >
                        <CrossIcon />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <button onClick={handleAddField} type="button" className={styles.textButton}>
              {values?.links_json.length > 0 ? t('common:buttons.addMore') : t('common:url')}
            </button>
          </div>
        )}
      />
    </div>
  );
};

export default AdditionalURL;
